import { useState } from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId, WorkshopParticipant } from "../../../api/workshop";
import type { WorkshopSmallTeam } from "../../../api/smallTeam";
import { useConfirmAction } from "../../../design-system/primitives/components/AlertDialog/useConfirmAction";
import DropdownMenu from "../../../design-system/primitives/components/DropdownMenu/DropdownMenu";
import Text from "../../../design-system/primitives/components/Text/Text";
import RenameGroupDialog from "./RenameGroupDialog";
import StudentRow from "./StudentRow";
import { useStudentDrop, type Group, type MoveStudent } from "./groups";
import styles from "./GroupBoard.css";

type GroupCardProps = {
  workshop: { id: WorkshopId };
  smallTeam: WorkshopSmallTeam;
  members: ReadonlyArray<WorkshopParticipant>;
  groups: ReadonlyArray<Group>;
  moveStudent: MoveStudent;
};

const GroupCard = ({
  workshop,
  smallTeam,
  members,
  groups,
  moveStudent,
}: GroupCardProps) => {
  const { isOver, drop } = useStudentDrop({
    targetGroupId: smallTeam.id,
    moveStudent,
  });

  const [renameOpen, setRenameOpen] = useState(false);

  const [doDeleteSmallTeam] = useMutation(MUTATIONS.deleteSmallTeam);
  const deleteGroup = useConfirmAction({
    onConfirm: () =>
      doDeleteSmallTeam({ workshopId: workshop.id, smallTeamId: smallTeam.id }),
    variant: "destructive",
    title: `"${smallTeam.name}" löschen?`,
    description:
      "Alle Schüler*innen, die aktuell noch in der Gruppe sind, verlieren dadurch ihre Gruppenzugehörigkeit.",
    errorDescription:
      "Die Gruppe konnte nicht gelöscht werden. Bitte versuche es erneut.",
    actionLabel: "Löschen",
    actionIcon: "trash",
  });

  return (
    <>
      <div ref={drop} className={styles.card({ isDropTarget: isOver })}>
        <div className={styles.cardHeader}>
          <Text variant="subheader-small" color="neutral-0">
            {smallTeam.name}
          </Text>
          <DropdownMenu
            triggerIcon="menu"
            triggerAriaLabel={`Optionen für ${smallTeam.name}`}
          >
            <DropdownMenu.Item
              icon="pencil"
              onSelect={() => setRenameOpen(true)}
            >
              Umbenennen
            </DropdownMenu.Item>
            <DropdownMenu.Item
              icon="trash"
              destructive
              onSelect={deleteGroup.open}
            >
              Löschen
            </DropdownMenu.Item>
          </DropdownMenu>
        </div>

        {members.length > 0 ? (
          <div className={styles.cardMembers}>
            {members.map((member) => (
              <StudentRow
                key={member.id}
                student={member}
                workshop={workshop}
                surface="nested"
                groups={groups}
                onMoveToGroup={(groupId) => moveStudent(member.id, groupId)}
              />
            ))}
          </div>
        ) : (
          <Text variant="body" color="neutral-100">
            Diese Gruppe ist noch leer
          </Text>
        )}
      </div>

      <RenameGroupDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        workshopId={workshop.id}
        smallTeamId={smallTeam.id}
        currentName={smallTeam.name}
      />

      {deleteGroup.dialogElement}
    </>
  );
};

type GroupBoardProps = {
  workshop: { id: WorkshopId };
  smallTeams: ReadonlyArray<WorkshopSmallTeam>;
  students: ReadonlyArray<WorkshopParticipant>;
  moveStudent: MoveStudent;
};

/**
 * The "2. Gruppen einteilen" panel's real content: one GroupCard per small
 * team, each a drop target, with its members' rows and a rename/delete menu.
 * Without groups, the drag hint is only shown while no students are invited
 * yet — once there are students, the panel stays empty.
 */
const GroupBoard = ({
  workshop,
  smallTeams,
  students,
  moveStudent,
}: GroupBoardProps) => {
  if (smallTeams.length === 0) {
    if (students.length > 0) return null;
    return (
      <div className={styles.emptyState}>
        <Text variant="body" color="neutral-100">
          Ziehe deine Schüler*innen hierher und manage deine Lerngruppen
        </Text>
      </div>
    );
  }

  const groups = smallTeams.map((team) => ({ id: team.id, name: team.name }));

  return (
    <div className={styles.groups}>
      {smallTeams.map((smallTeam) => (
        <GroupCard
          key={smallTeam.id}
          workshop={workshop}
          smallTeam={smallTeam}
          members={students.filter(
            (student) => student.smallTeamId === smallTeam.id,
          )}
          groups={groups}
          moveStudent={moveStudent}
        />
      ))}
    </div>
  );
};

export default GroupBoard;
